import { useMemo, type ReactNode } from 'react';
import { airportPlaceName } from '@voll-intelligence/genui-contract';
import { FlightDetailPanel } from '@voll-intelligence/intelligence-ui-kit';
import type { FlightDetailSummary } from '../../shared/ui/action.js';
import { flightDetailCopy } from '../../shared/ui/flight_detail_copy.js';
import { useGenUiI18n } from '../../shared/ui/i18n.js';
import type { FlightLeg } from './flight_offers_port.js';
import { FlightSeatMapPanel, FlightSeatMapSection } from './flight_seat_map_section.js';
import { useFlightLegs } from './use_flight_legs.js';

export interface FlightDetailDrawerContainerProps {
  /** `offerId` opaco — keyea o GET /flight/legs lazy. */
  offerId: string;
  /** Cabeçalho (cia/voo/rota), mostrado antes das pernas carregarem. */
  summary: FlightDetailSummary;
  onClose: () => void;
  /** Rodapé "Selecionar voo" — ausente ⇒ sem rodapé (card congelado/leitura). */
  onSelect?: () => void;
  /** Por qual porta o drawer foi aberto: `'seats'` (o link "Assentos" do card
   *  de checkout, #5331) rola até a seção do mapa quando as pernas pousam. */
  focus?: 'seats';
}

/** O slot de assentos: a seção sob o detalhe, e o mapa cheio trocando o corpo do drawer. */
function renderSeatMap(
  ready: readonly FlightLeg[],
  openPanel: (panel: ReactNode) => void,
): ReactNode {
  return (
    <FlightSeatMapSection
      legs={ready}
      onOpen={(query, view) => {
        openPanel(<FlightSeatMapPanel query={query} view={view} />);
      }}
    />
  );
}

/**
 * O `FlightDetailPanel` do kit (#4997) ligado ao que só o genui-kit tem: as
 * pernas pelo port `flightOffers` do `GenUiConfig`, os textos do catálogo, o nome
 * dos aeroportos do domain e o mapa de assentos, que fica aqui e entra por slot.
 */
export function FlightDetailDrawerContainer({
  offerId,
  ...props
}: Readonly<FlightDetailDrawerContainerProps>) {
  const { locale } = useGenUiI18n();
  const copy = useMemo(() => flightDetailCopy(locale), [locale]);
  const legs = useFlightLegs(offerId);
  return (
    <FlightDetailPanel
      {...props}
      legs={legs}
      copy={copy}
      locale={locale}
      airportName={airportPlaceName}
      renderSeats={renderSeatMap}
    />
  );
}
